import { useMemo, type CSSProperties } from "react";
import QRCode from "qrcode";
import {
  getLabelSize,
  labelSizeLabel,
  resolveZplTemplate,
  useLabelZplTemplates,
  type LabelSizeId,
  type StorageBinLabelData,
} from "@/labels";
import { parseZplLayout, type ZplElement } from "@/labels/preview/zplLayout";

/**
 * Print-accurate preview of a storage-bin label (FIX-318, label print fidelity
 * plan 2026-07-31). It draws the same ZPL template the Zebra prints (the
 * company's override if it has one), at the label's real size in mm: white,
 * black, square edges, no theme. Browser printing prints it at that size.
 */
export function StorageBinLabelPreview({
  data,
  size,
  showSize = true,
}: {
  data: StorageBinLabelData;
  size: LabelSizeId;
  showSize?: boolean;
}) {
  const { templates } = useLabelZplTemplates();
  const spec = getLabelSize(size);
  const layout = useMemo(
    () => parseZplLayout(resolveZplTemplate("storage-bin", size, templates), spec),
    [size, templates, spec],
  );
  const mm = (dots: number) => `${(dots * 25.4) / spec.dpi}mm`;

  const values: Record<string, string> = {
    binCode: data.binCode,
    // Same defaults as renderStorageBinZpl.
    locationName: data.locationName || "—",
    description: data.description || "",
    footer: data.footer ?? "",
    qrValue: data.qrValue,
  };

  return (
    <figure className="inline-flex flex-col items-start gap-1 m-0 print:break-inside-avoid">
      <div
        className="relative overflow-hidden bg-white text-black border border-black [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        style={{ width: mm(layout.widthDots), height: mm(layout.heightDots) }}
        aria-label={`${data.binCode} label`}
      >
        {layout.elements.map((el, i) => (
          <PreviewElement key={i} el={el} values={values} mm={mm} />
        ))}
      </div>
      {showSize ? (
        <figcaption className="text-xs text-muted-foreground print:hidden">{labelSizeLabel(size)}</figcaption>
      ) : null}
    </figure>
  );
}

function PreviewElement({
  el,
  values,
  mm,
}: {
  el: ZplElement;
  values: Record<string, string>;
  mm: (dots: number) => string;
}) {
  if (el.kind === "box") {
    const filled = el.thickness >= Math.min(el.width, el.height);
    return (
      <div
        className="absolute"
        style={{
          left: mm(el.x),
          top: mm(el.y),
          width: mm(el.width),
          height: mm(el.height),
          ...(filled ? { background: "#000" } : { border: `${mm(el.thickness)} solid #000` }),
        }}
      />
    );
  }

  if (el.kind === "qr") {
    return <PreviewQr value={values[el.placeholder] ?? ""} x={el.x} y={el.y} magnification={el.magnification} mm={mm} />;
  }

  const text = values[el.placeholder] ?? "";
  if (!text) return null;
  // ^A0N,h,w: h is the character height, w the width. Scale horizontally by
  // w/h so condensed fonts look condensed, and widen the box by h/w so a
  // ^FB block still wraps at its printed width.
  const scaleX = el.fontWidth / el.fontHeight;
  const lineHeight = el.fontHeight + (el.block?.lineSpacing ?? 0);
  const style: CSSProperties = {
    left: mm(el.x),
    top: mm(el.y),
    fontSize: mm(el.fontHeight),
    lineHeight: mm(lineHeight),
    color: el.reverse ? "#fff" : "#000",
    transform: `scaleX(${scaleX})`,
    transformOrigin: "left top",
    fontFamily: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif',
    fontWeight: 700,
  };
  if (el.block) {
    Object.assign(style, {
      width: mm(el.block.width / scaleX),
      maxHeight: mm(lineHeight * el.block.maxLines),
      overflow: "hidden",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: el.block.maxLines,
      overflowWrap: "anywhere",
    } satisfies CSSProperties);
  } else {
    style.whiteSpace = "nowrap";
  }
  return (
    <div className="absolute" style={style}>
      {text}
    </div>
  );
}

function PreviewQr({
  value,
  x,
  y,
  magnification,
  mm,
}: {
  value: string;
  x: number;
  y: number;
  magnification: number;
  mm: (dots: number) => string;
}) {
  // Same QR the Zebra draws: ^FDMA = error correction M, automatic mode.
  const path = useMemo(() => {
    if (!value) return null;
    const { modules } = QRCode.create(value, { errorCorrectionLevel: "M" });
    let d = "";
    for (let r = 0; r < modules.size; r++)
      for (let c = 0; c < modules.size; c++) if (modules.get(r, c)) d += `M${c} ${r}h1v1h-1z`;
    return { d, n: modules.size };
  }, [value]);
  if (!path) return null;
  return (
    <svg
      className="absolute"
      style={{ left: mm(x), top: mm(y), width: mm(path.n * magnification), height: mm(path.n * magnification) }}
      viewBox={`0 0 ${path.n} ${path.n}`}
      shapeRendering="crispEdges"
      aria-hidden
    >
      <path d={path.d} fill="#000" />
    </svg>
  );
}
